<template>
    <div v-if="isSystem && !props.frontend" class="chat-message-system">
        <!-- eslint-disable -->
        <span class="chat-message-system-content" v-html="message.message"></span>
        <!-- eslint-enable -->
    </div>
    <div v-if="isAuto" class="chat-message-auto">
        <!-- eslint-disable -->
        <span class="chat-message-auto-content" v-html="message.message"></span>
        <!-- eslint-enable -->
    </div>
    <div
        v-else-if="!isSystem"
        class="chat-message"
        :class="[isSender ? 'chat-message-reverse' : '']"
        :title="dateTimeHumanReadableFromISO(message.created_at)"
    >
        <div
            class="chat-message-avatar"
            :class="[isSender ? 'chat-message-avatar--right' : 'chat-message-avatar--left']"
        >
            <span
                class="chat-message-avatar-image-wrapper"
                :class="[
                    isSender ? 'chat-message-avatar-image-wrapper--user' : 'chat-message-avatar-image-wrapper--guest',
                ]"
            >
                <svg
                    class="chat-message-avatar-image"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    :class="[isSender ? 'chat-message-avatar-image--user' : 'chat-message-avatar-image--guest']"
                >
                    <path
                        d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                </svg>
            </span>
        </div>
        <div class="chat-message-content">
            <template v-if="!message.compound">
                <!-- Message single -->
                <div :class="[isSender ? 'chat-message-content-wrapper--user' : 'chat-message-content-wrapper--guest']">
                    <div :class="[message.user_id ? 'chat-message-content--user' : 'chat-message-content--guest']">
                        {{ message.message }}
                    </div>
                </div>
            </template>
            <template v-else>
                <!-- Group of messages -->
                <div
                    v-for="(component, componentIndex) in message.components"
                    :key="componentIndex"
                    :class="[isSender ? 'chat-message-content-wrapper--user' : 'chat-message-content-wrapper--guest']"
                >
                    <div
                        class="chat-message-content--compound"
                        :class="[
                            isSender ? 'chat-message-content--user' : 'chat-message-content--guest',
                            componentIndex === 0 ? 'chat-message-content--compound-first' : '',
                            componentIndex === message.components.length - 1
                                ? 'chat-message-content--compound-last'
                                : '',
                        ]"
                    >
                        {{ component }}
                    </div>
                </div>
            </template>
            <div class="chat-message-time" :class="[isSender ? 'chat-message-time--right' : 'chat-message-time--left']">
                {{ relativeTime(message.created_at) }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { dateTimeHumanReadableFromISO, relativeTime } from '@src/Common/Helpers/dates';
import { toRefs, computed } from 'vue';
import { ChatMessage } from '@src/Chat/Pages/types';

interface Props {
    message?: ChatMessage;
    frontend?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    message: null,
    frontend: false,
});

const isSystem = computed(() => props.message.type === 'system');
const isAuto = computed(() => props.message.type === 'auto');

const isSender = computed(() => {
    return (props.message.user_id && !props.frontend) || (!props.message.user_id && props.frontend);
});

toRefs(props);
</script>

<style scoped>
.chat-message {
    display: flex;
    align-items: flex-end;
}

.chat-message-reverse {
    flex-direction: row-reverse;
}

.chat-message-avatar {
    padding-bottom: 1.25rem;
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    flex: none;
}

.chat-message-avatar--right {
    margin-left: 0.5rem;
}

.chat-message-avatar--left {
    margin-right: 0.5rem;
}

.chat-message-avatar-image-wrapper {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    overflow: hidden;
    border-radius: 9999px;
}

.chat-message-avatar-image-wrapper--user {
    background-color: #dbeafe;
}

.chat-message-avatar-image-wrapper--guest {
    background-color: #f3f4f6;
}

.chat-message-avatar-image {
    width: 100%;
    height: 100%;
}

.chat-message-avatar-image--user {
    color: #93c5fd;
}

.chat-message-avatar-image--guest {
    color: #d1d5db;
}

.chat-message-content {
    line-height: 1.25rem;
    font-size: 0.875rem;
    flex: 1 1 0%;
}

.chat-message-content--user {
    display: inline-block;
    background-color: #1d4ed8;
    color: #fff;
    border-radius: 1rem;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding: 0.4rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
}

.chat-message-content--guest {
    display: inline-block;
    background-color: #e5e7eb;
    color: #111827;
    border-radius: 1rem;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    padding: 0.4rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
}

.chat-message-content-wrapper--user {
    text-align: right;
}

.chat-message-content-wrapper--guest {
    text-align: left;
}

.chat-message-time {
    opacity: 0.5;
    font-size: 0.75rem;
    line-height: 1rem;
    margin-top: 0.25rem;
}

.chat-message-time--right {
    text-align: right;
}

.chat-message-time--left {
    text-align: left;
}

.chat-message-content--compound {
    margin-top: 1px;
}
.chat-message-content-wrapper--user .chat-message-content--compound-first {
    border-top-right-radius: 1rem;
}

.chat-message-content-wrapper--user .chat-message-content--compound-last {
    border-bottom-right-radius: 1rem;
}

.chat-message-content-wrapper--guest .chat-message-content--compound-first {
    border-top-left-radius: 1rem;
}

.chat-message-content-wrapper--guest .chat-message-content--compound-last {
    border-bottom-left-radius: 1rem;
}

.chat-message-system {
    text-align: center;
    line-height: 1.15rem;
    font-size: 0.75rem;
    padding: 0.4rem 0;
}

.chat-message-system-content {
    display: inline-block;
    border-radius: 2rem;
    border: 1px solid #d1d5db;
    padding: 0.2rem 0.75rem;
    max-width: 90%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.chat-message-system + .chat-message-system {
    padding-top: 0;
}

.chat-message-auto {
    text-align: center;
    line-height: 1.15rem;
    font-size: 0.85rem;
    padding: 0.4rem 0;
}

.chat-message-auto-content {
    display: inline-block;
    border-radius: 2rem;
    border: 1px solid #d1d5db;
    background-color: #f2f2f2;
    padding: 0.5rem 0.75rem;
    max-width: 90%;
}
</style>

<style>
.chat-message-system-content a {
    text-decoration: underline;
    color: #3b82f6;
}

.chat-message-system-content a:hover {
    text-decoration: none;
}

.chat-message-auto-content a {
    text-decoration: underline;
    color: #3b82f6;
}

.chat-message-auto-content a:hover {
    text-decoration: none;
}
</style>
